/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { gql } from '@apollo/client';
import {
  renderDefaultError,
  renderDefaultLoading,
  cond,
  SmartComponentProps
} from '@exo/frontend-common-utils';
import { useProductSearch } from '../../hooks/useProductSearch';
import { processProductList } from '../../common/productListing';
import { Product } from '../../model/types';
import { useSessionContext } from '@exo/frontend-common-session-context';
import { useEffect, useMemo } from 'react';
import { usePriceRange,useOnSaleBuyPriceRange } from '../../hooks/usePriceRange';

export const SearchListingContainer = ({
  render,
  renderLoading = renderDefaultLoading,
  currency,
  term,
  selectedFacets,
  sort,
  storeId,
  useReviews,
  useAvailability
}: Props) => {
  const session = useSessionContext();

  const { loading, error, data, onLoadMore } = useProductSearch<ProductResponse>(
    { searchTerm: term, facets: selectedFacets, sort, currency },
    SearchListingContainer.fragment(useAvailability, useReviews, storeId)
  );

  const productData = useMemo(()=>{
    return processProductList(data?.products?.edges!,session?.pricingData ? session?.pricingData : [], sort)
  },[data?.products?.edges!,session?.pricingData?session?.pricingData:[],sort])
  
  const listOfPartNumbers =
    productData && productData?.length
      ? productData?.map(eachProductData => {
          return eachProductData?.partnumber;
        })
      : [];

  const {data: dataOnSalePrice, refetch: refetchOnsale } = useOnSaleBuyPriceRange(listOfPartNumbers, 'CAD', { fetchPolicy: 'network-only' });

  const {
    loading: loadingPrice,
    // error: errorPrice,
    data: dataPrice,
    refetch
  } = usePriceRange(
    listOfPartNumbers,
    'CAD',
    { fetchPolicy: 'network-only' },
    listOfPartNumbers?.length === 0
  );

   useEffect(() => {
    const timeoutId = setTimeout(() => {
      session?.set({ pricingData: dataPrice?.price?.entitledPrice });
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [term, dataPrice]);

  useEffect(() => {
    if(listOfPartNumbers?.length !== 0){
      refetch();
      refetchOnsale();
    }
  }, [session?.targetTimer]);

  if (loading || loadingPrice) return renderLoading();

  // const productData = processProductList(data?.products?.edges!);

  const hasMore = !!data?.products?.pageInfo?.hasNextPage;
  return render({
    productData,
    onLoadMore,
    hasMore,
    totalResultsCount: data?.products?.pageInfo?.totalResultsCount,
    prices: dataPrice?.price?.entitledPrice,
    onSaleBuyPrice : dataOnSalePrice
  });
};

type ProductResponse = {
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string;
    totalResultsCount: number;
  };
  edges: {
    cursor: string;
    node: Product;
  }[];
};

SearchListingContainer.fragment = (availability, reviews, storeId) => gql`
  fragment SearchList on PrdResultConnection {
    pageInfo {
      hasNextPage
      endCursor
      totalResultsCount
    }
    edges {
      cursor
      node {
        id
        partnumber
        slug
        name
        thumbnail
        topParentCatName
        type
        attributes {
          id
          value {
            value
          }
        }
        price {
          list {
            value
            currency
          }
          offer {
            value
            currency
          }
        }

        ${cond(
          reviews,
          `
          reviews {
            averageRating
            reviewCount
          }
        `
        )}

        ${cond(
          availability,
          `
          availability {
            status
            shipNode {
              id
              name
            }
            distributionGroup {
              id
            }
          }
        `
        )}
      }
    }
  }
`;

type Props = SmartComponentProps<{
  term: string;
  currency: string;
  useReviews?: boolean;
  useAvailability?: 'online' | 'store' | 'online+store';
  storeId?: string;
  selectedFacets?: string[];
  sort?: string;
  render: (props: SearchListingContainerRenderProps) => JSX.Element;
}>;

export type SearchListingContainerRenderProps = {
  onLoadMore: () => void;
  hasMore: boolean;
  totalResultsCount?: number;
  productData: ReturnType<typeof processProductList>;
  prices: any;
  onSaleBuyPrice?: any;
};
